import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import '../styles/dluo.css';

const VOLUMES = ['10ml', '50ml', 'autre'];
const NICOTINES = ['0mg', '3mg', '6mg', '10mg', '11mg', '12mg', '16mg', '18mg', '20mg', 'autre'];
const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function getMonthRange() {
  const now = new Date();
  const months = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({ month: d.getMonth(), year: d.getFullYear(), key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` });
  }
  return months;
}

function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function fmtDateTime(d) {
  if (!d) return '';
  const dt = new Date(d + 'Z');
  return dt.toLocaleDateString('fr-FR') + ' ' + dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function isHighVolume(vol) {
  const n = parseInt(vol);
  return !isNaN(n) && n > 10;
}

export default function DluoPage() {
  const { isAdmin, canViewStore } = useAuth();
  const toast = useToast();
  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({ designation: '', volume: '10ml', volumeCustom: '', nicotine: '0mg', nicotineCustom: '', dluo_date: '', quantity: 1 });

  // Reason popup
  const [reasonPopup, setReasonPopup] = useState(null); // { type: 'edit'|'delete', product, data? }
  const [reason, setReason] = useState('');

  // History modal
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const months = getMonthRange();

  useEffect(() => { loadStores(); }, []);

  const loadStores = async () => {
    try {
      const data = (await api.getStores()).filter(store => canViewStore('dluo', store.id));
      setStores(data);
      if (data.length > 0) {
        const sid = parseInt(localStorage.getItem('currentStoreId'));
        setCurrentStore(previous => data.find(s => s.id === previous?.id) || (sid ? data.find(s => s.id === sid) : null) || data[0]);
      } else {
        setCurrentStore(null);
      }
    } catch (e) { console.error(e); }
  };

  const loadProducts = useCallback(async () => {
    if (!currentStore) return;
    setLoading(true);
    try { setProducts(await api.dluoGetAll(currentStore.id)); }
    catch (e) { toast('Erreur chargement', 'error'); }
    setLoading(false);
  }, [currentStore, toast]);

  useEffect(() => { if (currentStore) loadProducts(); }, [currentStore, loadProducts]);

  const handleStoreChange = (store) => { setCurrentStore(store); localStorage.setItem('currentStoreId', store.id); };

  const getVolume = () => form.volume === 'autre' ? `${form.volumeCustom}ml` : form.volume;
  const getNicotine = () => {
    if (isHighVolume(getVolume())) return '0mg';
    return form.nicotine === 'autre' ? `${form.nicotineCustom}mg` : form.nicotine;
  };

  const openAdd = (monthKey) => {
    const [y, m] = monthKey.split('-');
    setForm({ designation: '', volume: '10ml', volumeCustom: '', nicotine: '0mg', nicotineCustom: '', dluo_date: `${y}-${m}-01`, quantity: 1 });
    setEditProduct(null);
    setShowAdd(monthKey);
  };

  const openEdit = (p) => {
    const volIsPreset = ['10ml', '50ml'].includes(p.volume);
    const nicIsPreset = ['0mg', '3mg', '6mg', '10mg', '11mg', '12mg', '16mg', '18mg', '20mg'].includes(p.nicotine);
    setForm({
      designation: p.designation, volume: volIsPreset ? p.volume : 'autre',
      volumeCustom: volIsPreset ? '' : parseInt(p.volume) || '',
      nicotine: nicIsPreset ? p.nicotine : 'autre',
      nicotineCustom: nicIsPreset ? '' : parseInt(p.nicotine) || '',
      dluo_date: p.dluo_date, quantity: p.quantity,
    });
    setEditProduct(p);
    const d = new Date(p.dluo_date);
    setShowAdd(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleSave = async () => {
    const vol = getVolume();
    const nic = getNicotine();
    if (!form.designation.trim() || !form.dluo_date) return toast('Désignation et DLUO requis', 'error');
    if (form.volume === 'autre' && (!form.volumeCustom || isNaN(form.volumeCustom))) return toast('Volume invalide', 'error');
    if (!isHighVolume(vol) && form.nicotine === 'autre' && (!form.nicotineCustom || isNaN(form.nicotineCustom))) return toast('Nicotine invalide', 'error');

    const payload = { designation: form.designation, volume: vol, nicotine: nic, dluo_date: form.dluo_date, quantity: parseInt(form.quantity) || 1 };

    if (editProduct) {
      // Need reason popup for edit
      setReasonPopup({ type: 'edit', product: editProduct, data: payload });
      setReason('');
    } else {
      // Direct add — no reason needed
      try {
        await api.dluoCreate({ ...payload, store_id: currentStore.id });
        toast('Produit ajouté', 'success');
        setShowAdd(null); setEditProduct(null);
        loadProducts();
      } catch (e) { toast(e.message, 'error'); }
    }
  };

  const confirmEdit = async () => {
    if (reason.trim().length < 5) return toast('La raison doit comporter au moins 5 caractères', 'error');
    try {
      await api.dluoUpdate(reasonPopup.product.id, { ...reasonPopup.data, reason });
      toast('Produit modifié', 'success');
      setReasonPopup(null); setReason(''); setShowAdd(null); setEditProduct(null);
      loadProducts();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleDelete = (p) => {
    setReasonPopup({ type: 'delete', product: p });
    setReason('');
  };

  const confirmDelete = async () => {
    if (reason.trim().length < 5) return toast('La raison doit comporter au moins 5 caractères', 'error');
    try {
      await api.dluoDelete(reasonPopup.product.id, reason);
      toast('Produit supprimé', 'success');
      setReasonPopup(null); setReason('');
      loadProducts();
    } catch (e) { toast(e.message, 'error'); }
  };

  const cancelForm = () => { setShowAdd(null); setEditProduct(null); };

  const handleVolumeChange = (val) => {
    const f = { ...form, volume: val };
    if (val !== 'autre' && isHighVolume(val)) { f.nicotine = '0mg'; f.nicotineCustom = ''; }
    setForm(f);
  };
  const handleVolumeCustomChange = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 2);
    const f = { ...form, volumeCustom: clean };
    if (clean && parseInt(clean) > 10) { f.nicotine = '0mg'; f.nicotineCustom = ''; }
    setForm(f);
  };
  const nicotineLocked = isHighVolume(getVolume());

  // History
  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try { setHistory(await api.dluoHistory(currentStore.id)); }
    catch (e) { toast('Erreur chargement historique', 'error'); }
    setHistoryLoading(false);
  };

  // Group products by month
  const grouped = {};
  months.forEach(m => { grouped[m.key] = []; });
  products.forEach(p => {
    const d = new Date(p.dluo_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (grouped[key]) grouped[key].push(p);
  });

  const isExpired = (dateStr) => { const t = new Date(); t.setHours(0,0,0,0); return new Date(dateStr) < t; };
  const isExpiringSoon = (dateStr) => { const diff = (new Date(dateStr) - new Date()) / 864e5; return diff >= 0 && diff <= 14; };
  const isExpiringMonth = (dateStr) => { const diff = (new Date(dateStr) - new Date()) / 864e5; return diff > 14 && diff <= 30; };

  return (
    <div className="dluo-page">
      {/* Store selector + history button */}
      <div className="dluo-top-bar">
        {stores.length > 1 && (
          <div className="dluo-store-bar">
            {stores.map(s => (
              <button key={s.id} className={`dluo-store-btn ${currentStore?.id === s.id ? 'active' : ''}`} onClick={() => handleStoreChange(s)}>
                🏪 {s.name}
              </button>
            ))}
          </div>
        )}
        {isAdmin && currentStore && (
          <button className="dluo-history-btn" onClick={openHistory}>📋 Historique</button>
        )}
      </div>

      <div className="dluo-layout">
        <div className="dluo-main">
          {loading ? <div className="dluo-loading">Chargement...</div> : (
            <div className="dluo-months">
              {months.map(m => {
                const items = grouped[m.key] || [];
                const isCurrentMonth = m.month === new Date().getMonth() && m.year === new Date().getFullYear();
                const totalQty = items.reduce((s, p) => s + p.quantity, 0);
                return (
                  <div key={m.key} className={`dluo-month-block ${isCurrentMonth ? 'current' : ''}`}>
                    <div className="dluo-month-header">
                      <div className="dluo-month-title">
                        <span className="dluo-month-name">{MONTH_NAMES[m.month]} {m.year}</span>
                        {items.length > 0 && <span className="dluo-month-count">{items.length} réf. · {totalQty} unités</span>}
                      </div>
                      <button className="dluo-add-btn" onClick={() => openAdd(m.key)} title="Ajouter">＋</button>
                    </div>
                    {items.length === 0 ? <div className="dluo-empty">Aucun produit pour ce mois</div> : (
                      <div className="dluo-table-wrap"><table className="dluo-table"><thead><tr>
                        <th style={{width:'80px'}}>Volume</th><th style={{width:'90px'}}>Nicotine</th><th>Désignation</th>
                        <th style={{width:'105px'}}>DLUO</th><th style={{width:'70px'}}>Nombre</th><th style={{width:'80px'}}></th>
                      </tr></thead><tbody>
                        {items.map(p => (
                          <tr key={p.id} className={isExpired(p.dluo_date) ? 'expired' : isExpiringSoon(p.dluo_date) ? 'expiring-soon' : isExpiringMonth(p.dluo_date) ? 'expiring-month' : ''}>
                            <td><span className="dluo-pill volume">{p.volume}</span></td>
                            <td><span className="dluo-pill nicotine">{p.nicotine}</span></td>
                            <td className="dluo-designation">{p.designation}</td>
                            <td className="dluo-date">{fmtDate(p.dluo_date)}</td>
                            <td className="dluo-qty">{p.quantity}</td>
                            <td className="dluo-actions">
                              <button className="dluo-action-btn" onClick={() => openEdit(p)} title="Modifier">✏️</button>
                              <button className="dluo-action-btn danger" onClick={() => handleDelete(p)} title="Supprimer">🗑️</button>
                            </td>
                          </tr>
                        ))}
                      </tbody></table></div>
                    )}
                    {/* Inline form */}
                    {showAdd === m.key && (
                      <div className="dluo-form">
                        <div className="dluo-form-title">{editProduct ? '✏️ Modifier' : '➕ Nouveau produit'}</div>
                        <div className="dluo-form-grid">
                          <div className="dluo-form-group">
                            <label>Volume</label>
                            <div className="dluo-form-with-custom">
                              <select value={form.volume} onChange={e => handleVolumeChange(e.target.value)}>
                                {VOLUMES.map(v => <option key={v} value={v}>{v === 'autre' ? 'Autre...' : v}</option>)}
                              </select>
                              {form.volume === 'autre' && <div className="dluo-custom-input"><input type="text" inputMode="numeric" maxLength={2} value={form.volumeCustom} onChange={e => handleVolumeCustomChange(e.target.value)} placeholder="00" /><span className="dluo-custom-suffix">ml</span></div>}
                            </div>
                          </div>
                          <div className="dluo-form-group">
                            <label>Nicotine {nicotineLocked && <span className="dluo-locked-hint">(fixé à 0mg)</span>}</label>
                            <div className="dluo-form-with-custom">
                              <select value={nicotineLocked ? '0mg' : form.nicotine} onChange={e => setForm({...form, nicotine: e.target.value, nicotineCustom: ''})} disabled={nicotineLocked}>
                                {NICOTINES.map(n => <option key={n} value={n}>{n === 'autre' ? 'Autre...' : n}</option>)}
                              </select>
                              {!nicotineLocked && form.nicotine === 'autre' && <div className="dluo-custom-input"><input type="text" inputMode="numeric" maxLength={2} value={form.nicotineCustom} onChange={e => setForm({...form, nicotineCustom: e.target.value.replace(/\D/g,'').slice(0,2)})} placeholder="00" /><span className="dluo-custom-suffix">mg</span></div>}
                            </div>
                          </div>
                          <div className="dluo-form-group wide">
                            <label>Désignation</label>
                            <input type="text" value={form.designation} onChange={e => setForm({...form, designation: e.target.value})} placeholder="Nom du e-liquide..." />
                          </div>
                          <div className="dluo-form-group">
                            <label>DLUO</label>
                            <input type="date" value={form.dluo_date} onChange={e => setForm({...form, dluo_date: e.target.value})} />
                          </div>
                          <div className="dluo-form-group">
                            <label>Nombre</label>
                            <input type="number" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} />
                          </div>
                        </div>
                        <div className="dluo-form-actions">
                          <button className="btn btn-secondary" onClick={cancelForm}>Annuler</button>
                          <button className="btn btn-primary" onClick={handleSave}>{editProduct ? 'Modifier' : 'Ajouter'}</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="dluo-legend">
          <div className="dluo-legend-card">
            <div className="dluo-legend-title">Légende des couleurs</div>
            <div className="dluo-legend-item"><span className="dluo-legend-dot expired"></span><div><strong>Rouge</strong><p>DLUO dépassée — à retirer du rayon</p></div></div>
            <div className="dluo-legend-item"><span className="dluo-legend-dot expiring-soon"></span><div><strong>Orange</strong><p>Expire dans moins de 14 jours — action urgente</p></div></div>
            <div className="dluo-legend-item"><span className="dluo-legend-dot expiring-month"></span><div><strong>Jaune</strong><p>Expire dans moins de 30 jours — à surveiller</p></div></div>
            <div className="dluo-legend-item"><span className="dluo-legend-dot ok"></span><div><strong>Normal</strong><p>DLUO valide, pas d'action nécessaire</p></div></div>
            <div className="dluo-legend-sep"></div>
            <div className="dluo-legend-info"><strong>ℹ️ Règle nicotine</strong><p>Les e-liquides de plus de 10ml sont obligatoirement à 0mg de nicotine (réglementation TPD).</p></div>
          </div>
        </div>
      </div>

      {/* ===== REASON POPUP ===== */}
      {reasonPopup && (
        <div className="modal-overlay" onClick={() => setReasonPopup(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: 440}}>
            <div className="modal-header">
              <h2>{reasonPopup.type === 'delete' ? '🗑️ Supprimer le produit' : '✏️ Modifier le produit'}</h2>
              <button className="modal-close" onClick={() => setReasonPopup(null)}>&times;</button>
            </div>
            <div style={{padding: 20}}>
              <div className="dluo-reason-product">
                <span className="dluo-pill volume">{reasonPopup.product.volume}</span>
                <span className="dluo-pill nicotine">{reasonPopup.product.nicotine}</span>
                <strong>{reasonPopup.product.designation}</strong>
              </div>
              <div className="form-group" style={{marginTop: 16}}>
                <label style={{display:'block', fontSize:'0.85rem', fontWeight:600, marginBottom:6}}>
                  {reasonPopup.type === 'delete' ? 'Raison de la suppression' : 'Raison de la modification'} <span style={{color:'var(--accent-red)'}}>*</span>
                </label>
                <textarea
                  className="dluo-reason-input"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Décrivez la raison (5 caractères min.)..."
                  rows={3}
                  autoFocus
                />
                {reason.length > 0 && reason.trim().length < 5 && (
                  <div style={{fontSize:'0.75rem', color:'var(--accent-red)', marginTop:4}}>Minimum 5 caractères ({reason.trim().length}/5)</div>
                )}
              </div>
              <div style={{display:'flex', gap:8, justifyContent:'flex-end', marginTop:16}}>
                <button className="btn btn-secondary" onClick={() => setReasonPopup(null)}>Annuler</button>
                <button
                  className={`btn ${reasonPopup.type === 'delete' ? 'btn-danger' : 'btn-primary'}`}
                  disabled={reason.trim().length < 5}
                  onClick={reasonPopup.type === 'delete' ? confirmDelete : confirmEdit}
                  style={reasonPopup.type === 'delete' ? {background:'var(--accent-red)', color:'white', border:'none', opacity: reason.trim().length < 5 ? 0.4 : 1} : {}}
                >
                  {reasonPopup.type === 'delete' ? 'Supprimer' : 'Confirmer la modification'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== HISTORY MODAL (admin only) ===== */}
      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal-content dluo-history-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Historique DLUO — {currentStore?.name}</h2>
              <button className="modal-close" onClick={() => setShowHistory(false)}>&times;</button>
            </div>
            <div style={{padding: 20}}>
              {historyLoading ? <div className="dluo-loading">Chargement...</div> : history.length === 0 ? (
                <div className="dluo-empty">Aucun historique</div>
              ) : (
                <div className="dluo-history-table-wrap">
                  <table className="dluo-table dluo-history-table">
                    <thead><tr>
                      <th style={{width:'130px'}}>Date / Heure</th>
                      <th style={{width:'100px'}}>Utilisateur</th>
                      <th style={{width:'95px'}}>Action</th>
                      <th style={{width:'70px'}}>Volume</th>
                      <th style={{width:'75px'}}>Nicotine</th>
                      <th>Désignation</th>
                      <th style={{width:'90px'}}>DLUO</th>
                      <th style={{width:'50px'}}>Nb</th>
                      <th>Raison</th>
                    </tr></thead>
                    <tbody>
                      {history.map(h => (
                        <tr key={h.id} className={`dluo-history-row ${h.action}`}>
                          <td className="dluo-date" style={{fontSize:'0.78rem'}}>{fmtDateTime(h.created_at)}</td>
                          <td style={{fontWeight:600, fontSize:'0.82rem'}}>{h.username}</td>
                          <td>
                            <span className={`dluo-action-badge ${h.action}`}>
                              {h.action === 'ajout' ? '➕' : h.action === 'modification' ? '✏️' : '🗑️'} {h.action}
                            </span>
                          </td>
                          <td><span className="dluo-pill volume">{h.volume}</span></td>
                          <td><span className="dluo-pill nicotine">{h.nicotine}</span></td>
                          <td className="dluo-designation">{h.designation}</td>
                          <td className="dluo-date">{h.dluo_date ? fmtDate(h.dluo_date) : ''}</td>
                          <td className="dluo-qty">{h.quantity}</td>
                          <td className="dluo-history-reason">{h.reason || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
